import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import { Notice } from '@/components/Modal';
import { markNoticeRead, useHrProfile, useUnlockHrSensitive, type HrSensitive } from '@/pages/home/useHrProfile';
import { useMyProfile } from '@/pages/home/useMyProfile';
import { useFormLedger } from '@/pages/approval/useApproval';
import { CareerStatementSection } from '@/pages/mypage/CareerStatementSection';
import { CertificateTab } from '@/pages/mypage/CertificateTab';
import { useMyCareers } from '@/pages/mypage/useMyPageExtras';
import { useMyLeave } from '@/pages/mypage/useLeave';
import { useMyCareer, type MyCareer } from '@/pages/mypage/useWelfare';

/**
 * 인사 탭 — 하위 탭 (사용자 지시 2026-09-22 · 근무처이력이 2026-09-29 에 붙었다).
 *
 * <pre>
 *   경력      확정된 경력 + 본인이 적어 내는 경력기술(증명서 첨부 · 인사팀 승인)
 *   근무처이력 언제부터 언제까지 어느 현장·부서에 있었나 (V349 · 인사팀이 넣고 본인은 읽는다)
 *   법정선임  선임된 직책 — <b>직책수당과 이어서</b> 보여 준다
 *   자격증    등록 · 자격점수 충족 여부 · 부족한 점수
 *   근속기간  입사일·근속 — <b>연차 기준</b>과 이어서 보여 준다
 *   인사고과  등급 누계 · 승진 기준 반영(세 축)
 *   명함신청  기안 + 내 신청 내역
 *   증명서    재직·경력증명서 발급 (2026-09-22 에 개인정보 설정에서 옮겨 왔다)
 * </pre>
 *
 * <p>증명서가 <b>명함신청 옆</b>인 것은 둘이 같은 부류이기 때문이다 — 인사 자료를 근거로
 * <b>종이를 받아 나가는 일</b>이다. 개인정보 설정의 모달에 있을 때는 계정·비밀번호와 한 칸에
 * 묶여 있어, 재직증명서를 떼려는 사람이 그 자리를 떠올리지 못했다(사용자 지시 2026-09-22).
 *
 * <h2>왜 탭으로 갈랐나</h2>
 * 한 화면에 이어 붙이니 <b>다섯 덩어리가 세로로 늘어선 긴 문서</b>가 됐다. 사람이 이 탭을 여는
 * 이유는 대개 하나다 — 자격증을 등록하러, 고과를 보러, 명함을 신청하러. 그 하나에 닿기까지
 * 스크롤을 세 번 하면 화면이 「찾는 곳」 이 아니라 「읽는 곳」 이 된다.
 *
 * <h2>근거는 옆에 붙인다</h2>
 * 갈라 놓되 <b>판정과 근거는 같은 탭에</b> 둔다: 법정선임 옆에 직책수당, 자격증 옆에 자격점수,
 * 근속 옆에 연차 기준, 고과 옆에 승진 요건. 「왜 대상이 아닌가」 를 알려고 탭을 옮기게 되면
 * 갈라 놓은 뜻이 없어진다.
 */

type SubTab = 'career' | 'worksite' | 'duty' | 'license' | 'tenure' | 'evaluation' | 'card' | 'certificate';

const SUB_TABS: Array<{ key: SubTab; label: string }> = [
  { key: 'career', label: '경력' },
  /*
    근무처이력 — <b>경력 바로 옆</b>이다(사용자 지시 2026-09-29). 둘은 같은 질문의 두 답이고
    ('내가 어디서 무엇을 했나'), 실제로 나란히 놓고 읽는다: 경력은 협회에 내는 <b>참여 사업</b>이고
    (발주처·공법·인정일수), 이쪽은 <b>인사 기록으로서의 근무처</b>라 본사 부서에 앉아 있던 기간도 선다.
  */
  { key: 'worksite', label: '근무처이력' },
  // 학력은 탭이 아니라 개인정보 설정의 「내 정보」 표 한 줄에서 고친다(사용자 지시 2026-11-02 · MyEducationRow).
  { key: 'duty', label: '법정선임' },
  { key: 'license', label: '자격증' },
  { key: 'tenure', label: '근속기간' },
  { key: 'evaluation', label: '인사고과' },
  { key: 'card', label: '명함신청' },
  { key: 'certificate', label: '증명서 발급' },
];

export function CareerTab() {
  /*
    서브탭도 <b>주소가 정할 수 있다</b>(`/mypage?tab=career&sub=certificate` · 사용자 지시 2026-10-22).
    사이드바 검색의 「증명서 발급」 줄이 이 칸으로 바로 도착한다. 모르는 값이면 조용히 경력이다.
  */
  const [params] = useSearchParams();
  const [sub, setSub] = useState<SubTab>(
    () => SUB_TABS.find((item) => item.key === params.get('sub'))?.key ?? 'career',
  );

  // 인사 탭에 이미 들어와 있는 사람에게도 링크가 듣게 한다(MyPage 의 같은 이유).
  useEffect(() => {
    const next = SUB_TABS.find((item) => item.key === params.get('sub'))?.key;
    if (next) setSub(next);
  }, [params]);
  const { data, isPending, error } = useMyCareer();

  /*
    재인증 결과는 <b>이 탭이</b> 쥔다. 탭을 떠나면 언마운트와 함께 사라져 저절로 다시 잠긴다 —
    캐시(TanStack Query)에 넣으면 화면을 떠난 뒤에도 등급이 메모리에 남는다.
  */
  const [sensitive, setSensitive] = useState<HrSensitive | null>(null);

  return (
    <div className="space-y-4">
      <div className="flex gap-1 flex-wrap" role="tablist">
        {SUB_TABS.map((item) => (
          <button
            key={item.key}
            role="tab"
            aria-selected={sub === item.key}
            onClick={() => setSub(item.key)}
            className="rounded-lg"
            style={{
              fontSize: '13px',
              padding: '5px 12px',
              fontWeight: sub === item.key ? 700 : 500,
              background: sub === item.key ? 'var(--accent-bg)' : 'transparent',
              color: sub === item.key ? 'var(--accent)' : 'var(--text-secondary)',
              border: `1px solid ${sub === item.key ? 'var(--accent-light)' : 'var(--border)'}`,
            }}
          >
            {item.label}
          </button>
        ))}
      </div>

      {sub === 'card' ? (
        <BusinessCardSection />
      ) : sub === 'certificate' ? (
        /*
          증명서 발급은 <b>인사 자료를 읽지 않는다</b> — 서식·용도·제출처를 받아 서버가 문서를
          굽는다({@code CertificateTab}). 그래서 위의 로딩·오류 분기(`useMyCareer`)를 기다릴
          이유가 없어 명함신청과 같은 자리에서 먼저 갈린다.
        */
        <CertificateTab />
      ) : isPending ? (
        <div className="flex justify-center py-12"><div className="spinner w-8 h-8" /></div>
      ) : error ? (
        <Notice tone="error">인사 자료를 불러오지 못했습니다.</Notice>
      ) : (
        <>
          {sub === 'career' && <CareerSection />}
          {sub === 'worksite' && <WorksiteSection worksites={data?.worksites ?? []} />}
          {sub === 'duty' && <DutySection duties={data?.duties ?? []} allowances={data?.allowances ?? []} />}
          {sub === 'license' && <LicenseSection licenses={data?.licenses ?? []} promotion={data?.promotion} />}
          {sub === 'tenure' && <TenureSection rankHistories={data?.rankHistories ?? []} allowances={data?.allowances ?? []} />}
          {sub === 'evaluation' && (
            <EvaluationSection sensitive={sensitive} onUnlock={setSensitive} promotion={data?.promotion} />
          )}
        </>
      )}
    </div>
  );
}

// ── 경력 ─────────────────────────────────────────────────────────────────────

/**
 * 확정된 경력 + 본인이 적어 내는 경력기술.
 *
 * <p>둘을 한 탭에 두는 이유는 <b>같은 것의 두 상태</b>이기 때문이다 — 아래에 적어 낸 문장이
 * 인사팀 확인을 거치면 위의 줄이 된다. 따로 두면 「냈는데 왜 안 보이지」 가 생긴다.
 */
function CareerSection() {
  const { data, isPending, error } = useMyCareers();
  const rows = [...(data ?? [])].sort((a, b) => (b.projectStart ?? '').localeCompare(a.projectStart ?? ''));

  return (
    <div className="space-y-5">
      <section>
        <h3 style={sectionTitle}>확정 경력 {rows.length > 0 ? `${rows.length}건` : ''}</h3>
        {isPending ? (
          <p style={mutedText}>불러오는 중입니다.</p>
        ) : error ? (
          <Notice tone="error">경력을 불러오지 못했습니다.</Notice>
        ) : rows.length === 0 ? (
          <p style={mutedText}>등록된 경력이 없습니다. 아래에서 경력기술을 내면 인사팀이 확인해 확정합니다.</p>
        ) : (
          rows.map((row) => (
            <div key={row.id} className="flex items-center gap-3 flex-wrap" style={rowStyle}>
              <span style={{ color: 'var(--text-muted)', minWidth: '150px' }}>
                {row.projectStart ?? ''}
                {row.projectStart || row.projectEnd ? ' ~ ' : ''}
                {row.projectEnd ?? '진행 중'}
              </span>
              <span style={{ fontWeight: 600 }}>{row.projectName ?? row.company ?? '-'}</span>
              {row.position && <span style={mutedText}>{row.position}</span>}
              {row.jobField && <span style={mutedText}>{row.jobField}</span>}
              {/*
                협회 신고상태를 함께 적는다 — 미신고 경력은 기술등급 승급이 미뤄지는 가장 흔한
                이유이고, 본인이 그 사실을 아는 자리가 여기 말고 없다.
              */}
              <span className="ml-auto">
                {row.reportStatus === 'APPROVED' ? (
                  <span className="badge badge-green">신고 완료</span>
                ) : row.reportStatus === 'UNREPORTED' ? (
                  <span className="badge badge-gray">미신고</span>
                ) : (
                  <span className="badge badge-yellow">{row.reportStatus}</span>
                )}
              </span>
            </div>
          ))
        )}
      </section>

      <CareerStatementSection />
    </div>
  );
}

// ── 근무처이력 ───────────────────────────────────────────────────────────────

/**
 * 근무처이력 — <b>언제부터 언제까지 어디에 있었나</b> (사용자 지시 2026-09-29).
 *
 * <h2>여기서 적지 않는다</h2>
 * 인사팀이 인사자료 관리에서 넣은 줄을 읽는다. 배치·발령과 같은 부류의 <b>확정된 인사 기록</b>이라
 * 본인이 고칠 자료가 아니고, 고치게 두면 경력증명서의 근거가 두 갈래가 된다.
 *
 * <h2>비어 있으면 그 사실만 적는다</h2>
 * 「인사팀에 요청하세요」 같은 지시를 두지 않는다 — 이력이 아직 안 들어온 것은 대부분 옛 기록을
 * 옮기는 중이라는 뜻이고, 본인이 채근해 해결되는 일이 아니다.
 */
function WorksiteSection({ worksites }: { worksites: MyCareer['worksites'] }) {
  return (
    <section>
      <h3 style={sectionTitle}>근무처이력 {worksites.length > 0 ? `${worksites.length}곳` : ''}</h3>
      {worksites.length === 0 ? (
        <p style={mutedText}>등록된 근무처이력이 없습니다.</p>
      ) : (
        worksites.map((row) => (
          <div key={`${row.placeName}-${row.startDate}`} className="flex items-center gap-3 flex-wrap" style={rowStyle}>
            <span style={{ color: 'var(--text-muted)', minWidth: '150px' }}>
              {row.startDate} ~ {row.endDate ?? '현재'}
            </span>
            <span style={{ fontWeight: 600 }}>{row.placeName}</span>
            {/* 현장인지 부서인지. 이름만으로는 갈리지 않는 경우가 있다(「기술연구소」). */}
            <span className="badge badge-gray" style={{ fontSize: '10px' }}>
              {row.placeType === 'site' ? '현장' : '부서'}
            </span>
            {row.roleTitle && <span style={mutedText}>{row.roleTitle}</span>}
            {row.note && <span className="ml-auto" style={mutedText}>{row.note}</span>}
          </div>
        ))
      )}
    </section>
  );
}

// ── 법정선임 ─────────────────────────────────────────────────────────────────

/**
 * 선임된 직책과 <b>직책수당</b> (사용자 지시 2026-09-22: 「법정선임 - 직책수당 연동」).
 *
 * <p>둘은 같은 사실의 두 얼굴이다 — 「소방안전관리자로 선임돼 신고 의무가 있다」 와 「그래서
 * 직책수당 대상이다」. 수당 판정은 복지 탭이 하지만, <b>근거를 보는 자리</b>는 여기다.
 */
function DutySection({ duties, allowances }: { duties: MyCareer['duties']; allowances: MyCareer['allowances'] }) {
  const dutyAllowances = allowances.filter((row) => row.kind === 'DUTY');

  return (
    <div className="space-y-5">
      <section>
        <h3 style={sectionTitle}>법정 선임</h3>
        {duties.length === 0 ? (
          <p style={mutedText}>선임된 직책이 없습니다.</p>
        ) : (
          duties.map((duty) => (
            <div key={duty.id} className="flex items-center gap-3 flex-wrap" style={rowStyle}>
              <span className="badge badge-blue">선임 중</span>
              <span style={{ fontWeight: 600 }}>{duty.dutyName}</span>
              <span style={mutedText}>{duty.placeName ?? ''} · {duty.appointedOn}~</span>
              {duty.licenseName && (
                <span style={mutedText}>
                  근거 {duty.licenseName}
                  {duty.licenseExpiry ? ` (${duty.licenseExpiry}까지)` : ''}
                </span>
              )}
              {/* 근거 자격증이 만료되면 선임이 유지될 수 없다 — 본인이 먼저 알아야 한다. */}
              {duty.licenseExpired && <span className="badge badge-yellow">자격증 만료</span>}
              <span className="ml-auto">
                {duty.reported ? (
                  <span className="badge badge-green">신고 완료</span>
                ) : (
                  <span className="badge badge-yellow">신고 전</span>
                )}
              </span>
            </div>
          ))
        )}
      </section>

      <section>
        <h3 style={sectionTitle}>직책수당</h3>
        {dutyAllowances.length === 0 ? (
          <p style={mutedText}>직책수당 기준이 등록되지 않았습니다.</p>
        ) : (
          dutyAllowances.map((allowance) => (
            <div key={allowance.name} className="flex items-center gap-3 flex-wrap" style={rowStyle}>
              <span style={{ fontWeight: 600, minWidth: '120px' }}>{allowance.name}</span>
              <span style={mutedText}>{allowance.reason ?? ''}</span>
              <span className="ml-auto">
                {allowance.eligible
                  ? <span className="badge badge-green">대상</span>
                  : <span className="badge badge-gray">미대상</span>}
              </span>
            </div>
          ))
        )}
        {/* 금액은 적지 않는다 — 통상임금이 시스템에 없어 곱하면 그것은 추정이고, 추정은 약속으로 읽힌다. */}
        <p style={{ ...mutedText, marginTop: '6px' }}>지급 금액과 반영 시점은 인사·급여 담당이 확정합니다.</p>
      </section>
    </div>
  );
}

// ── 자격증 ───────────────────────────────────────────────────────────────────

/**
 * 자격증 등록 · 자격점수 (사용자 지시 2026-09-22: 「자격증 - 등록, 자격점수 충족여부, 부족점수 알림」).
 *
 * <p>점수는 <b>승진 판정이 세는 그 값</b>이다({@code promotion.currentLicenseScore}) — 여기서 따로
 * 계산하지 않는다. 두 곳에서 세면 한쪽이 먼저 갈라지고, 사람은 큰 쪽을 믿는다.
 */
function LicenseSection({
  licenses,
  promotion,
}: {
  licenses: MyCareer['licenses'];
  promotion: MyCareer['promotion'] | undefined;
}) {
  const navigate = useNavigate();
  const score = promotion?.currentLicenseScore ?? null;
  const required = promotion?.requiredLicenseScore ?? null;
  const shortfall = score != null && required != null ? Math.max(0, required - score) : null;

  return (
    <div className="space-y-5">
      <section>
        <div className="flex items-center gap-2">
          <h3 style={sectionTitle}>자격증</h3>
          <button
            className="btn-secondary ml-auto"
            style={compactButton}
            onClick={() => navigate('/approval/draft?formKey=LICENSE_REGISTER')}
          >
            자격증 등록 신청
          </button>
        </div>
        {licenses.length === 0 ? (
          <p style={mutedText}>등록된 자격증이 없습니다.</p>
        ) : (
          licenses.map((license) => (
            <div key={license.id} className="flex items-center gap-3 flex-wrap" style={rowStyle}>
              <span style={{ fontWeight: 600, minWidth: '160px' }}>{license.name}</span>
              <span style={mutedText}>{license.issuer ?? ''}</span>
              <span style={mutedText}>{license.acquiredDate ?? ''}</span>
              <span className="ml-auto flex items-center gap-2">
                {license.expiryDate && <span style={mutedText}>{license.expiryDate}까지</span>}
                {license.expired ? (
                  <span className="badge badge-yellow">만료</span>
                ) : license.daysUntilExpiry != null && license.daysUntilExpiry <= 90 ? (
                  <span className="badge badge-yellow">D-{license.daysUntilExpiry}</span>
                ) : null}
              </span>
            </div>
          ))
        )}
      </section>

      <section>
        <h3 style={sectionTitle}>자격점수</h3>
        {promotion?.atTop ? (
          // 꼭대기 직급은 승진 요건이 없다 — 점수를 「부족」 으로 적으면 없는 목표를 만든다.
          <p style={mutedText}>승진 요건이 없는 직급이라 자격점수를 따지지 않습니다.</p>
        ) : promotion?.licenseTableMissing ? (
          <Notice tone="info">자격 점수표가 등록되지 않았습니다. 인사팀이 넣으면 충족 여부가 계산됩니다.</Notice>
        ) : score == null || required == null ? (
          <p style={mutedText}>승진 기준이 등록되지 않아 자격점수를 계산하지 않습니다.</p>
        ) : (
          <>
            <div className="flex items-center gap-3 flex-wrap" style={rowStyle}>
              <span style={{ fontWeight: 600, width: '96px' }}>{score}점 / {required}점</span>
              {shortfall === 0 ? (
                <span className="badge badge-green">충족</span>
              ) : (
                /* 「부족점수 알림」 — 얼마나 모자란지를 숫자로 말한다. 「부족」 한 마디로는 무엇을 더
                   따야 하는지 가늠할 수 없다. */
                <span className="badge badge-yellow">{shortfall}점 부족</span>
              )}
              <span style={mutedText}>{promotion?.toRank ? `${promotion.toRank} 승진 기준` : ''}</span>
            </div>
            {promotion != null && promotion.licenseUnmatched > 0 && (
              <p style={mutedText}>
                점수표에 없는 자격증 {promotion.licenseUnmatched}건은 점수에 세지 않았습니다 —
                인사팀이 점수표에 그 종목을 넣으면 합계가 올라갑니다.
              </p>
            )}
          </>
        )}
      </section>
    </div>
  );
}

// ── 근속기간 ─────────────────────────────────────────────────────────────────

/**
 * 근속과 <b>연차 기준</b> (사용자 지시 2026-09-22: 「근속기간 - 연차관리 연동 / 연차 기준 설명, 산정」).
 *
 * <p>연차 일수는 근속에서 나온다. 그런데 그 둘이 다른 탭에 있어서 「내 연차가 왜 15일인가」 에
 * 답하는 자리가 없었다 — 여기서 <b>근속 → 부여 일수</b>를 한 줄로 잇는다.
 *
 * <p>숫자는 연차관리와 <b>같은 응답</b>에서 온다({@code useMyLeave}). 여기서 다시 세지 않는다.
 */
function TenureSection({
  rankHistories,
  allowances,
}: {
  rankHistories: MyCareer['rankHistories'];
  allowances: MyCareer['allowances'];
}) {
  const { person } = useMyProfile();
  const { data: leave } = useMyLeave();
  const tenureAllowances = allowances.filter((row) => row.kind === 'TENURE');
  const current = leave?.current ?? null;

  return (
    <div className="space-y-5">
      <section>
        <h3 style={sectionTitle}>근속</h3>
        <div className="flex items-center gap-3 flex-wrap" style={rowStyle}>
          <span style={{ color: 'var(--text-muted)', width: '72px' }}>입사일</span>
          <span style={{ fontWeight: 600 }}>{person?.joinDate ?? '-'}</span>
          <span style={{ color: 'var(--text-muted)', width: '52px' }}>근속</span>
          <span style={{ fontWeight: 600 }}>{tenure(person?.joinDate)}</span>
        </div>
      </section>

      <section>
        <h3 style={sectionTitle}>연차 기준</h3>
        {current || leave?.fiscal?.total != null ? (
          <>
            <div className="flex items-center gap-3 flex-wrap" style={rowStyle}>
              <span style={{ color: 'var(--text-muted)', width: '72px' }}>이번 회계</span>
              {/* 숫자는 회계 기준(연차관리 표와 같은 값 · DOC-113). 재시작 전 서버면 입사일 연차연도로 물러선다. */}
              {leave?.fiscal ? (
                <>
                  <span style={mutedText}>{leave.fiscal.year}-01-01 ~ {leave.fiscal.year}-12-31</span>
                  <span style={{ fontWeight: 600 }}>부여 {leave.fiscal.total ?? '-'}일</span>
                  <span style={mutedText}>사용 {leave.fiscal.usedDays}일</span>
                  <span className="ml-auto" style={{ fontWeight: 700 }}>잔여 {leave.fiscal.remainingDays ?? '-'}일</span>
                </>
              ) : current ? (
                <>
                  <span style={mutedText}>{current.startDate} ~ {current.endDate}</span>
                  <span style={{ fontWeight: 600 }}>부여 {current.grantedDays}일</span>
                  <span style={mutedText}>사용 {current.usedDays}일</span>
                  <span className="ml-auto" style={{ fontWeight: 700 }}>잔여 {current.remainingDays}일</span>
                </>
              ) : null}
            </div>
            {leave?.standard?.accrualBasis && (
              // accrualBasis 는 문장이 아니라 코드다(V76 CHECK: JOIN_DATE · FISCAL_YEAR). 이름표는 LeaveStandardPanel 과 같다.
              <p style={mutedText}>
                {leave.standard.accrualBasis === 'JOIN_DATE' ? '입사일 기준' : '회계연도 기준'}
              </p>
            )}
            {leave?.standard && !leave.standard.confirmed && (
              <p style={mutedText}>인사팀이 확정하기 전의 <b>잠정 기준</b>입니다.</p>
            )}
          </>
        ) : (
          <p style={mutedText}>연차 원장이 아직 없습니다. 「연차관리」 탭에서 자세히 볼 수 있습니다.</p>
        )}
      </section>

      {tenureAllowances.length > 0 && (
        <section>
          <h3 style={sectionTitle}>근속수당</h3>
          {tenureAllowances.map((allowance) => (
            <div key={allowance.name} className="flex items-center gap-3 flex-wrap" style={rowStyle}>
              <span style={{ fontWeight: 600, minWidth: '120px' }}>{allowance.name}</span>
              <span style={mutedText}>{allowance.reason ?? ''}</span>
              <span className="ml-auto">
                {allowance.eligible
                  ? <span className="badge badge-green">대상</span>
                  : <span className="badge badge-gray">미대상</span>}
              </span>
            </div>
          ))}
        </section>
      )}

      {rankHistories.length > 0 && (
        <section>
          <h3 style={sectionTitle}>직급 이력</h3>
          {rankHistories.map((row) => (
            <div key={`${row.rankName}-${row.effectiveDate}`} className="flex items-center gap-3" style={rowStyle}>
              <span style={{ color: 'var(--text-muted)', width: '96px' }}>{row.effectiveDate}</span>
              <span style={{ fontWeight: 600 }}>{row.rankName}</span>
              <span style={mutedText}>{row.reason ?? ''}</span>
            </div>
          ))}
        </section>
      )}
    </div>
  );
}

// ── 인사고과: 비밀번호를 다시 확인하고 연다 ──────────────────────────────────
//
// 잠금은 화면이 아니라 **서버**가 건다 — 비밀번호를 넣기 전에는 등급이 브라우저에 오지 않는다
// (`POST /v1/home/hr/unlock`). 화면에서 감추기만 하면 응답에 이미 값이 있어 개발자 도구로 보인다.
//
// 막으려는 것은 로그인한 채 자리를 비운 화면이다. 세션을 훔친 사람을 막지는 못한다.

/**
 * 「여기까지 봤다」 를 찍는다 — <b>재인증을 통과해 자료가 손에 들어온 뒤</b>다.
 * 탭을 누른 순간에 찍으면 비밀번호 창에서 취소한 사람의 새 고과 알림이 사라진다.
 */
function useMarkReadOnUnlock(unlocked: boolean) {
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!unlocked) return;
    markNoticeRead('EVALUATION');
    // 홈으로 돌아갔을 때 그 줄이 접혀 있어야 한다. 다시 읽지 않으면 다음 새로고침까지 남는다.
    void queryClient.invalidateQueries({ queryKey: ['home-hr'] });
  }, [unlocked, queryClient]);
}

/**
 * 인사고과 — 등급 이력 · <b>누계</b> · 승진 기준 반영
 * (사용자 지시 2026-09-22: 「인사고과 - 누계, 승진 기준 반영」).
 */
function EvaluationSection({
  sensitive,
  onUnlock,
  promotion,
}: {
  sensitive: HrSensitive | null;
  onUnlock: (data: HrSensitive) => void;
  promotion: MyCareer['promotion'] | undefined;
}) {
  const profile = useHrProfile();
  useMarkReadOnUnlock(Boolean(sensitive));

  const available = profile.data?.hasEvaluations ?? false;

  return (
    <div className="space-y-5">
      <section>
        <h3 style={sectionTitle}>등급</h3>
        {!available ? (
          <p style={mutedText}>등록된 인사고과가 없습니다.</p>
        ) : !sensitive ? (
          <PasswordGate onUnlock={onUnlock} />
        ) : sensitive.evaluations.length === 0 ? (
          <p style={mutedText}>등록된 인사고과가 없습니다.</p>
        ) : (
          sensitive.evaluations.map((evaluation) => (
            <div key={evaluation.year} className="flex items-center gap-3" style={rowStyle}>
              <span style={{ fontWeight: 600, width: '52px' }}>{evaluation.year}</span>
              <span className="badge badge-blue" style={{ fontSize: '12px', flexShrink: 0 }}>{evaluation.grade}</span>
              {evaluation.score != null && (
                <span style={{ color: 'var(--text-secondary)' }}>{evaluation.score.toFixed(1)}점</span>
              )}
              {evaluation.note && (
                <span className="flex-1 truncate text-right" style={mutedText}>{evaluation.note}</span>
              )}
            </div>
          ))
        )}
      </section>

      <PromotionSection promotion={promotion} />
    </div>
  );
}

/** 잠금 화면. 이 탭을 벗어나면 다시 잠긴다. */
function PasswordGate({ onUnlock }: { onUnlock: (data: HrSensitive) => void }) {
  const unlock = useUnlockHrSensitive();
  const [password, setPassword] = useState('');

  const submit = () => {
    if (!password.trim() || unlock.isPending) return;
    unlock.mutate(password, {
      onSuccess: (data) => {
        setPassword('');
        onUnlock(data);
      },
    });
  };

  return (
    <div className="py-3" style={{ maxWidth: '340px' }}>
      <p style={mutedText}>민감정보입니다. 본인 확인을 위해 비밀번호를 입력해 주세요.</p>
      <div className="flex gap-2" style={{ marginTop: '8px' }}>
        <input
          className="input-field"
          type="password"
          autoComplete="current-password"
          aria-label="비밀번호"
          placeholder="비밀번호"
          value={password}
          onChange={(event) => {
            setPassword(event.target.value);
            unlock.reset();
          }}
          onKeyDown={(event) => event.key === 'Enter' && submit()}
        />
        <button className="btn-primary flex-shrink-0" onClick={submit} disabled={!password.trim() || unlock.isPending}>
          {unlock.isPending ? '확인 중...' : '조회'}
        </button>
      </div>
      {unlock.isError && <p style={{ fontSize: '12px', color: 'var(--danger)', marginTop: '6px' }}>{unlock.error.message}</p>}
    </div>
  );
}

/**
 * 승진 요건 — 세 축을 <b>각각</b> 말한다. 종합 판정 한 줄로 뭉치면 무엇을 채워야 하는지가
 * 사라진다. 기준표에 없는 직급은 아예 진단하지 않는다: 「기준이 없으니 통과」 로 내리면 화면이
 * 승진 가능하다고 말한다.
 */
function PromotionSection({ promotion }: { promotion: MyCareer['promotion'] | undefined }) {
  if (!promotion) return null;
  // 꼭대기 직급은 승진 요건 칸을 내지 않는다 — 「기준 미등록」 은 누가 빠뜨렸다는 뜻으로 읽힌다.
  if (promotion.atTop) return null;
  if (!promotion.available) {
    return (
      <section>
        <h3 style={sectionTitle}>승진 요건</h3>
        <Notice tone="info">{promotion.reason ?? '승진 기준이 등록되지 않았습니다.'}</Notice>
      </section>
    );
  }

  return (
    <section>
      <h3 style={sectionTitle}>
        {promotion.toRank} 승진 요건{' '}
        <span style={mutedText}>
          (현재 {promotion.fromRank}
          {/* 학력별 기준이면 그 사실을 말한다 — 사원→주임은 고졸·초대졸·대졸의 기간이 다르다. */}
          {promotion.appliedEducation ? ` · ${promotion.appliedEducation} 기준` : ''})
        </span>
      </h3>

      <Axis
        no={1}
        label="근속"
        ok={promotion.rankOk}
        value={`현직급 ${
          promotion.currentRankYears != null ? `${promotion.currentRankYears}년` : months(promotion.currentRankMonths)
        } / ${months(promotion.requiredRankMonths)}`}
        hint={!promotion.rankOk && promotion.reviewYear != null ? `${promotion.reviewYear}년 심사 대상` : undefined}
        note={promotion.rankMonthsAssumed ? '직급 이력이 없어 입사일로 셌습니다.' : undefined}
      />
      {/*
        고과는 **점수의 합**이다(V93) — 등급마다 점수를 주고(S 5 · A 4 · B 3 · C -1 · D -5) 그 합을
        본다. 현 직급에서 쌓은 것만 세므로, 승진하면 0에서 다시 시작한다. 그 <b>누계</b>가 이 줄이다.
      */}
      <Axis
        no={2}
        label="인사고과"
        ok={promotion.evaluationOk}
        value={
          promotion.evaluationTableMissing
            ? '등급 환산표가 등록되지 않았습니다'
            : `${promotion.currentEvaluationScore ?? 0}점 / ${promotion.requiredEvaluationScore ?? 0}점`
        }
        hint={
          promotion.evaluationTableMissing || promotion.evaluationCounted === 0
            ? undefined
            : `현 직급 ${promotion.evaluationCounted}개년 누계`
        }
        note={
          promotion.evaluationUnmatched > 0
            ? `환산표에 없는 등급 ${promotion.evaluationUnmatched}건은 점수에 세지 않았습니다.`
            : undefined
        }
      />
      <Axis
        no={3}
        label="자격점수"
        ok={promotion.licenseOk}
        value={
          promotion.licenseTableMissing
            ? '자격 점수표가 등록되지 않았습니다'
            : `${promotion.currentLicenseScore ?? 0}점 / ${promotion.requiredLicenseScore ?? 0}점`
        }
        note={promotion.licenseUnmatched > 0 ? `점수표에 없는 자격증 ${promotion.licenseUnmatched}건은 세지 않았습니다.` : undefined}
      />
    </section>
  );
}

function Axis({
  no,
  label,
  ok,
  value,
  hint,
  note,
}: {
  no: number;
  label: string;
  ok: boolean;
  value: string;
  hint?: string;
  note?: string;
}) {
  return (
    <div style={{ padding: '7px 0', borderBottom: '1px solid var(--border)' }}>
      <div className="flex items-center gap-3" style={{ fontSize: '13px' }}>
        <span style={{ color: 'var(--text-muted)', width: '18px' }}>{no}</span>
        <span style={{ fontWeight: 600, width: '72px' }}>{label}</span>
        <span>{value}</span>
        <span className="ml-auto">
          {ok ? <span className="badge badge-green">충족</span> : <span className="badge badge-yellow">부족</span>}
        </span>
      </div>
      {(hint || note) && (
        <p style={{ ...mutedText, marginLeft: '90px' }}>{[hint, note].filter(Boolean).join(' · ')}</p>
      )}
    </div>
  );
}

// ── 명함신청 ─────────────────────────────────────────────────────────────────

/**
 * 명함 신청 (사용자 지시 2026-09-22).
 *
 * <h2>칸을 여기서 받지 않는다</h2>
 * 명함은 <b>결재 서식</b>이다({@code BUSINESS_CARD}) — 기안 화면이 인사 프로필·배치에서 값을
 * 채워 주고 사람은 확인만 한다. 여기에 입력칸을 다시 세우면 그 채움을 두 벌로 만들게 되고,
 * 오타가 유일한 실패 모드인 서식에서 그것은 곧 잘못 찍힌 명함이 된다.
 *
 * <h2>내역은 결재 문서를 그대로 읽는다</h2>
 * 새 표를 세우지 않는다 — 대장({@code hr_business_card_orders})은 총무의 취합용이고, 본인이
 * 궁금한 것은 「내가 낸 것이 지금 어디까지 갔나」 다. 그 답은 결재 문서에 이미 있다.
 */
function BusinessCardSection() {
  const navigate = useNavigate();
  const ongoing = useFormLedger('BUSINESS_CARD', false);
  const done = useFormLedger('BUSINESS_CARD', true);

  const rows = [...(ongoing.data ?? []), ...(done.data ?? [])];

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <h3 style={sectionTitle}>명함 신청</h3>
        <button
          className="btn-primary ml-auto"
          style={compactButton}
          onClick={() => navigate('/approval/draft?formKey=BUSINESS_CARD')}
        >
          명함 신청
        </button>
      </div>
      <p style={mutedText}>
        기안 화면이 이름·직급·소속·연락처를 인사 자료에서 채웁니다. 적힌 그대로 인쇄되니 확인 후 올려 주세요.
      </p>

      {ongoing.isPending ? (
        <p style={mutedText}>불러오는 중입니다.</p>
      ) : rows.length === 0 ? (
        <p style={mutedText}>신청 내역이 없습니다.</p>
      ) : (
        rows.map((row) => (
          <div key={row.id} className="flex items-center gap-3 flex-wrap" style={rowStyle}>
            {/* 올린 날. 임시저장은 상신 전이라 비어 있다 — 그때는 문서번호도 없다. */}
            <span style={{ color: 'var(--text-muted)', minWidth: '96px' }}>{row.submittedAt?.slice(0, 10) ?? ''}</span>
            <span style={{ fontWeight: 600 }}>{row.title}</span>
            {row.docNo && <span style={mutedText}>{row.docNo}</span>}
            <span className="ml-auto badge badge-gray">{row.status}</span>
          </div>
        ))
      )}
    </div>
  );
}

function months(value: number | null | undefined): string {
  if (value == null) return '-';
  const years = Math.floor(value / 12);
  const rest = value % 12;
  return years > 0 ? `${years}년 ${rest}개월` : `${rest}개월`;
}

/** `7년 4개월`. 입사일이 없으면 계산하지 않는다. */
function tenure(joinDate: string | null | undefined): string {
  if (!joinDate) return '-';

  const start = new Date(`${joinDate}T00:00:00`);
  if (Number.isNaN(start.getTime())) return '-';

  const now = new Date();
  let total = (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth());
  if (now.getDate() < start.getDate()) total -= 1;
  if (total < 0) return '-';

  return months(total);
}

const compactButton = { fontSize: '12px', padding: '5px 12px' } as const;
const mutedText = { fontSize: '12px', color: 'var(--text-muted)' } as const;
const sectionTitle = { fontSize: '13px', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '4px' } as const;
const rowStyle = { padding: '7px 0', borderBottom: '1px solid var(--border)', fontSize: '13px' } as const;
